import { useMemo } from "react";
import { X, Copy, Download, Printer, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[130px_1fr] gap-2 border-b border-white/5 py-1 text-xs">
    <span className="font-mono uppercase tracking-wider text-white/40">{k}</span>
    <span className="break-all font-mono text-white/85">{v ?? "—"}</span>
  </div>
);

/**
 * View Error Details — the large, screenshot-friendly failure window for
 * uploads, ZIP extraction and backup imports. Every field is already
 * server-redacted; the correlation ID matches the backend log line.
 *
 * error: {
 *   title, stage, detail, correlationId, name, size, received, offset, chunk,
 *   httpStatus, retries, durationMs, lastStep
 * }
 */
const ErrorDetailsDialog = ({ error, onRetry, onClose }) => {
  const report = useMemo(() => {
    if (!error) return "";
    const L = [];
    L.push("ELITE MECHANICAL — UPLOAD/IMPORT DIAGNOSTIC REPORT");
    L.push(`Generated: ${new Date().toISOString()}`);
    L.push(`Correlation ID: ${error.correlationId || "n/a"}  (grep backend logs for this)`);
    L.push("");
    L.push(`What failed: ${error.title || "Upload"}`);
    L.push(`Stage: ${error.stage || "unknown"}`);
    L.push(`Summary: ${error.detail || "unknown"}`);
    L.push("");
    if (error.name) L.push(`File: ${error.name}`);
    if (error.size != null) L.push(`File size: ${error.size.toLocaleString()} bytes`);
    if (error.received != null) L.push(`Confirmed bytes: ${error.received.toLocaleString()} (${error.size ? Math.round((error.received / error.size) * 100) : 0}%)`);
    if (error.offset != null) L.push(`Last failed offset: ${error.offset.toLocaleString()}`);
    if (error.chunk != null) L.push(`Chunk: ${error.chunk}`);
    if (error.httpStatus != null) L.push(`HTTP status: ${error.httpStatus}`);
    if (error.retries != null) L.push(`Retries: ${error.retries}`);
    if (error.durationMs != null) L.push(`Duration: ${Math.round(error.durationMs / 1000)}s`);
    if (error.lastStep) L.push(`Last successful step: ${error.lastStep}`);
    L.push(`Browser: ${navigator.userAgent}`);
    L.push(`Online at report time: ${navigator.onLine}`);
    L.push("");
    L.push("Secrets, tokens and signed URLs are redacted by the server before display.");
    return L.join("\n");
  }, [error]);

  if (!error) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
      toast.success("Error details copied");
    } catch { toast.error("Copy failed — use Download instead"); }
  };
  const download = () => {
    const blob = new Blob([report], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `diagnostic-${error.correlationId || Date.now()}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };
  const print = () => {
    const w = window.open("", "_blank", "width=700,height=800");
    if (!w) return;
    w.document.write(`<pre style="font:12px monospace;white-space:pre-wrap">${report.replace(/</g, "&lt;")}</pre>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-3" data-testid="takeoff-error-details">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-red-500/30 bg-[#0b0b0f] text-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md border border-red-500/40 bg-red-500/10 text-red-400">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-tight">View Error Details</h3>
              <p className="font-mono text-[10px] text-white/40">correlation ID: {error.correlationId || "n/a"}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="error-details-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <p className="mb-3 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm leading-snug text-red-200" data-testid="error-details-summary">
            {error.detail}
          </p>
          <Row k="Operation" v={error.title} />
          <Row k="Stage" v={error.stage} />
          <Row k="File" v={error.name} />
          <Row k="File size" v={error.size != null ? `${error.size.toLocaleString()} bytes` : null} />
          <Row k="Confirmed bytes" v={error.received != null ? `${error.received.toLocaleString()} bytes` : null} />
          <Row k="Failed offset" v={error.offset != null ? error.offset.toLocaleString() : null} />
          <Row k="Chunk" v={error.chunk} />
          <Row k="HTTP status" v={error.httpStatus} />
          <Row k="Retries" v={error.retries} />
          <Row k="Last good step" v={error.lastStep} />
          <Row k="Time" v={new Date().toLocaleString()} />
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-white/10 px-4 py-3">
          {onRetry && (
            <button onClick={onRetry} data-testid="error-details-retry" className="inline-flex items-center gap-1.5 rounded-md bg-[#f97316] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#ea580c]">
              <RotateCcw className="h-3.5 w-3.5" /> Retry from failed step
            </button>
          )}
          <button onClick={copy} data-testid="error-details-copy" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10">
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
          <button onClick={download} data-testid="error-details-download" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10">
            <Download className="h-3.5 w-3.5" /> Download
          </button>
          <button onClick={print} data-testid="error-details-print" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10">
            <Printer className="h-3.5 w-3.5" /> Print
          </button>
          <button onClick={onClose} className="ml-auto rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:bg-white/10">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ErrorDetailsDialog;
